import { Component, OnInit } from '@angular/core';
import { CreditNoteService } from '../../services/credit-note.service';
import { BanksService } from '../../services/banks.service';
import { LoadingService } from '../../services/loading.service';
import { ToastService } from '../../services/toast.service';

@Component({
    selector: 'app-credit-notes',
    templateUrl: './credit-notes.component.html',
    styleUrls: ['./credit-notes.component.scss']
})
export class CreditNotesComponent implements OnInit {
    // Datos paginados
    paginatedNotes: any[] = [];

    // Paginación
    pageNumber: number = 1;
    pageSize: number = 20;
    totalRecords: number = 0;

    // Filtros
    searchTerm: string = '';
    selectedBank: any = null;
    startDate: Date | null = null;
    endDate: Date | null = null;
    dateType: string = 'Creation';
    dateTypeOptions = [
        { label: 'Fecha de Creación', value: 'Creation' },
        { label: 'Fecha de Documento', value: 'Document' }
    ];
    selectedCancellationFilter: string = 'active';

    // Ordenamiento
    sortOrder: 'asc' | 'desc' = 'desc';

    // Métricas
    totalCredits: number = 0;
    monthlyAverage: number = 0;
    lastMonthTotal: number = 0;
    banks: any[] = [];

    // PDF
    isDownloadingPdf: { [key: number]: boolean } = {};

    // Modal de anulación
    showConfirmationModal = false;
    cancellationReason = '';
    selectedNoteForCancellation: any = null;

    // Timeout para debounce de búsqueda
    private searchTimeout: any;

    constructor(
        private creditNoteService: CreditNoteService,
        private banksService: BanksService,
        private loadingService: LoadingService,
        private toastService: ToastService
    ) { }

    ngOnInit(): void {
        this.loadBanks();
        this.loadData();
    }

    private loadBanks(): void {
        this.banksService.getAll().subscribe({
            next: (response) => {
                // Manejar estructura paginada: data puede ser array directo o {data: [...], totalCount: ...}
                const banksData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
                this.banks = banksData;
            },
            error: (err) => {
                console.error('Error loading banks:', err);
            }
        });
    }

    private loadData(): void {
        this.loadingService.show();
        
        const parameters = {
            pageNumber: this.pageNumber,
            pageSize: this.pageSize,
            searchTerm: this.searchTerm,
            sortBy: 'Id',
            sortOrder: this.sortOrder,
            startDate: this.startDate,
            endDate: this.endDate,
            dateType: this.dateType,
            bankId: this.selectedBank,
            cancellationFilter: this.selectedCancellationFilter
        };

        this.creditNoteService.getPagedAsync(parameters).subscribe({
            next: (response) => {
                this.paginatedNotes = response.data || [];
                this.totalRecords = response.totalRecords || 0;
                this.calculateMetrics();
                this.loadingService.hide();
            },
            error: (err) => {
                console.error(err);
                this.toastService.showToast('error', 'Error al cargar las notas crédito');
                this.loadingService.hide();
            }
        });
    }

    private calculateMetrics(): void {
        // Calcular métricas desde los datos paginados
        this.totalCredits = this.paginatedNotes.reduce((sum, n) => sum + n.amount, 0);
        
        // Calcular total del último mes
        const lastMonth = new Date();
        lastMonth.setMonth(lastMonth.getMonth() - 1);
        this.lastMonthTotal = this.paginatedNotes
            .filter(n => new Date(n.documentDate) >= lastMonth)
            .reduce((sum, n) => sum + n.amount, 0);
    }

    onFilterChange(): void {
        // Debounce para búsqueda
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }
        
        this.searchTimeout = setTimeout(() => {
            this.pageNumber = 1; // Reset a primera página al filtrar
            this.loadData();
        }, 300);
    }

    onCancellationFilterChange(): void {
        this.pageNumber = 1;
        this.loadData();
    }

    onPageChange(event: any): void {
        this.pageNumber = (event.first / event.rows) + 1;
        this.pageSize = event.rows;
        this.loadData();
    }

    toggleSortOrder(): void {
        this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
        this.loadData();
    }

    downloadPdf(note: any): void {
        this.isDownloadingPdf[note.id] = true;
        
        this.creditNoteService.downloadPdf(note.id).subscribe({
            next: (blob) => {
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `NotaCredito_${note.id}_${new Date().getTime()}.pdf`;
                link.click();
                window.URL.revokeObjectURL(url);
                this.isDownloadingPdf[note.id] = false;
                this.toastService.showToast('success', 'PDF descargado correctamente');
            },
            error: (error) => {
                console.error('Error downloading PDF:', error);
                this.isDownloadingPdf[note.id] = false;
                this.toastService.showToast('error', 'Error al descargar el PDF');
            }
        });
    }

    // Métodos de anulación
    showCancelModal(note: any): void {
        if (note.isCancelled) {
            this.toastService.showToast('warning', 'Esta nota ya está anulada');
            return;
        }
        this.selectedNoteForCancellation = note;
        this.cancellationReason = '';
        this.showConfirmationModal = true;
    }

    async onConfirmCancellation(): Promise<void> {
        if (!this.cancellationReason?.trim()) {
            this.toastService.showToast('error', 'Debe especificar el motivo de anulación');
            return;
        }
        if (!this.selectedNoteForCancellation) return;

        try {
            this.loadingService.show();
            await this.creditNoteService
                .cancel(this.selectedNoteForCancellation.id, this.cancellationReason.trim())
                .toPromise();
            this.toastService.showToast('success', 'Nota crédito anulada exitosamente');
            this.closeConfirmationModal();
            this.loadData();
        } catch (error: any) {
            console.error('Error al anular nota crédito:', error);
            const errorMessage = error?.error?.errorMessage || error?.error || 'Error al anular la nota crédito';
            this.toastService.showToast('error', errorMessage);
        } finally {
            this.loadingService.hide();
        }
    }

    onCancelCancellation(): void {
        this.closeConfirmationModal();
    }

    private closeConfirmationModal(): void {
        this.showConfirmationModal = false;
        this.selectedNoteForCancellation = null;
        this.cancellationReason = '';
    }
}
